import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { ArrowBack, ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Avatar,
  Button,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

import { useAppSelector } from 'state/hooks';
import {
  UserProjectSubscription,
  useGetUserSubscriptionsQuery,
} from 'state/userAdminSlices';
import { kUserStatusLoggedIn, selectUserState } from 'state/userSlice';

import { useAppDispatchUserStatus } from 'app/AppDispatcher';
import { WholeScreenModalHeader } from 'features/app_header/AppHeader';
import { ProjectStatusBadge } from 'features/user_admin/ProjectStatusBadge';
import { subscriptionHomeUrl } from 'features/user_admin/common';

import styles from './UserProjectsPanel.module.css';

export function UserProjectsSubscriptionList() {
  const { sub_id } = useParams();
  const navigate = useNavigate();
  const { currentData: subscriptionData } = useGetUserSubscriptionsQuery();
  const subscriptions = subscriptionData?.subscriptions;
  const [collapseSingles, setCollapseSingles] = useState(false);
  const [collapseMultis, setCollapseMultis] = useState(false);

  useEffect(() => {
    if (subscriptions && subscriptions.length > 0 && !sub_id) {
      navigate(`${subscriptions[0].subscription_id}`);
    }
  }, [subscriptions]);

  if (!subscriptions) {
    return null;
  }

  const isSubNonSingleton = (sub: UserProjectSubscription): boolean =>
    Boolean(
      sub.can_create ||
      !!sub.template ||
      sub.projects.length !== 1 ||
      sub.project_requests.length > 0,
    );

  const singleton_subscriptions = subscriptions.filter(
    (sub) => !isSubNonSingleton(sub),
  );
  const multi_subscriptions = subscriptions.filter((sub) =>
    isSubNonSingleton(sub),
  );

  const renderMultiSubscription = (sub: UserProjectSubscription, i) => {
    const isSelected = sub.subscription_id === sub_id;
    return (
      <ListItem
        key={sub.subscription_id}
        selected={isSelected}
        autoFocus={isSelected}
        className={
          isSelected
            ? styles.subscription_collapser_selected
            : styles.subscription_collapser
        }
        secondaryAction={
          <Avatar className={styles.multi_sub_project_count}>
            {sub.projects.length + sub.project_requests.length}
          </Avatar>
        }
      >
        <ListItemButton
          onClick={() => navigate(subscriptionHomeUrl(sub.subscription_id))}
        >
          <ListItemText primary={sub.subscription_name} />
        </ListItemButton>
      </ListItem>
    );
  };

  const renderSingletonSub = (sub: UserProjectSubscription) => {
    const isSelected = sub.subscription_id === sub_id;
    const proj = sub.projects[0];
    return (
      <ListItem
        key={sub.subscription_id}
        selected={isSelected}
        autoFocus={isSelected}
        className={
          isSelected
            ? styles.subscription_collapser_selected
            : styles.subscription_collapser
        }
        secondaryAction={
          <Typography>
            <ProjectStatusBadge status={proj.status} micro />
          </Typography>
        }
      >
        <ListItemButton
          onClick={() => navigate(subscriptionHomeUrl(sub.subscription_id))}
        >
          {sub.subscription_name}
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Stack justifyContent="space-between">
      <List className={styles.project_list_container}>
        {singleton_subscriptions.length > 0 && (
          <>
            <ListItem
              secondaryAction={
                <IconButton
                  onClick={() => setCollapseSingles(!collapseSingles)}
                >
                  {!collapseSingles ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              }
            >
              <Typography
                variant="h2"
                className={styles.subscription_type_title}
              >
                Single project subscriptions{' '}
                <span className={styles.project_count_text}>
                  ({singleton_subscriptions.length})
                </span>
              </Typography>
            </ListItem>
            <Collapse in={!collapseSingles}>
              <List>{singleton_subscriptions.map(renderSingletonSub)}</List>
            </Collapse>
          </>
        )}
        {multi_subscriptions.length !== 0 &&
          singleton_subscriptions.length !== 0 && <Divider />}
        {multi_subscriptions.length > 0 && (
          <>
            <ListItem
              secondaryAction={
                <IconButton onClick={() => setCollapseMultis(!collapseMultis)}>
                  {!collapseMultis ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              }
            >
              <Typography
                variant="h2"
                className={styles.subscription_type_title}
              >
                Multi project subscriptions
              </Typography>
            </ListItem>
            <Collapse in={!collapseMultis}>
              <List>{multi_subscriptions.map(renderMultiSubscription)}</List>
            </Collapse>
          </>
        )}
        {multi_subscriptions.length === 0 &&
          singleton_subscriptions.length === 0 && (
            <Typography>No subscriptions available</Typography>
          )}
      </List>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/app')}
        sx={{ width: 'auto', marginRight: 'auto' }}
        className={styles.back_to_app_button}
      >
        Return to app
      </Button>
    </Stack>
  );
}

export function UserProjectsPanel({
  children,
  allowOverflow = false,
}: React.PropsWithChildren & { allowOverflow?: boolean }) {
  const userInfo = useAppSelector(selectUserState);
  const navigate = useNavigate();
  const { refetch } = useGetUserSubscriptionsQuery();
  useAppDispatchUserStatus();

  useEffect(() => {
    refetch();
  }, [refetch]);

  if (
    userInfo &&
    userInfo.status === kUserStatusLoggedIn &&
    !userInfo.has_sub_admin
  ) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="App">
      <WholeScreenModalHeader close={() => navigate('/app')}>
        <Typography variant="h1" className={styles.page_title}>
          Manage Subscriptions
        </Typography>
      </WholeScreenModalHeader>
      <div className="App-body">
        <Stack className={styles.admin_panel_container}>
          <Stack direction="row" className={styles.admin_panel_subcontainer}>
            <UserProjectsSubscriptionList />
            {children && (
              <Stack className={styles.full_width_content_container_outer}>
                <Stack
                  className={styles.full_width_content_container}
                  style={!allowOverflow ? { maxHeight: '100%' } : {}}
                >
                  {children}
                </Stack>
              </Stack>
            )}
          </Stack>
        </Stack>
      </div>
    </div>
  );
}
